import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Grid, TextField, CircularProgress } from '@mui/material';
import { createPortal } from 'react-dom';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import SearchIcon from '@mui/icons-material/Search';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { AlertMessagesContext } from 'react-alert-messages';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { saveAs } from 'file-saver';
import CsvHelper from 'helpers/CsvHelper';
import SyncIcon from '@mui/icons-material/Sync';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import PurchaseSummaryReportServices from 'services/PurchaseSummaryReportServices';
import PrintIcon from '@mui/icons-material/Print';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';

function PurchaseItemSummary() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());

  const [searchText, setSearchText] = useState('');
  const [purchases, setPurchase] = useState('');
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    date: tCommon('Date'),
    plu: tCommon('PLU'),
    name: tCommon('Name'),
    hsn: tCommon('HSN'),
    rate: tCommon('Rate'),
    quantity: tCommon('Quantity'),
    tax: {
      title: tCommon('Total Tax'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.tax || 0);
      },
    },
    total: {
      title: tCommon('Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total || 0);
      },
    },
  };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(purchases, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `PURCHASE-ITEMS-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const _getPurchasesReportSummart = (purchases) => {
    let totalAmount = 0;
    let totalTax = 0;
    purchases.forEach((purchase) => {
      totalAmount += purchase?.total;
      totalTax += purchase?.tax;
    });
    return {
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
    };
  };

  const headerData = [
    {
      label: tCommon('PLU'),
      id: 'plu',
      type: 'text',
      viewRender: (item) => item?.vendor?.name ?? item?.vendor_name_old ?? '-',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Purchase Date'),
      id: 'date',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.formatDateTime(item.date),
    },
    {
      label: tCommon('Rate'),
      id: 'rate',
      type: 'text',
    },
    {
      label: tCommon('Quantity'),
      id: 'quantity',
      type: 'text',
    },
    {
      label: tCommon('Total Tax'),
      id: 'tax',
      type: 'text',
    },
    {
      label: tCommon('Total Amount'),
      id: 'total',
      type: 'floatAmount',
    },
  ];

  const getPurchase = useCallback(async () => {
    setLoadingIndicator(true);
    try {
      const { startDate, endDate } = dynamicDateRange('PURCHASE_ITEM_SUMMARY');
      setFilterFromDate(startDate);
      setFilterToDate(endDate);

      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(startDate),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(endDate),
      };

      const res = await PurchaseSummaryReportServices.getPurchaseItemSummary(data);
      setPurchase(res);
      setFilteredPurchases(res);
      setPrevFilterData({
        filterFromDate: startDate,
        filterToDate: endDate,
      });
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  }, [postAlertMessage]);

  const generatePurchaseHistory = () => {
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate,
      filterToDate,
    });
    getPurchase();
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate.getTime() !== prevFilterData.filterFromDate.getTime() ||
      filterToDate.getTime() !== prevFilterData.filterToDate.getTime()
    );
  }, [filterFromDate, filterToDate, prevFilterData]);

  useEffect(() => {
    generatePurchaseHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Purchase Item Summary Report'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredPurchases,
      {
        [tCommon('Total Tax')]: summary.totalTax,
        [tCommon('Total Amount')]: summary.totalAmount,
      }
    );
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredPurchases(
      (purchases || []).filter(
        (purchase) => purchase.name.toLowerCase().includes(value) || purchase.date.includes(value)
      )
    );
  };

  const summary = _getPurchasesReportSummart(filteredPurchases);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a', marginRight: 5 }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            Csv
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <Grid container spacing={1} alignItems="flex-end">
          <Grid item xs={12}>
            <h2 className={styles.subTitle}>
              {tReport('Purchase Item Summary Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </Grid>
          <Grid item xs={12} md={3}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="PURCHASE_ITEM_SUMMARY"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={tCommon('Search purchase')}
              value={searchText}
              onChange={(e) => {
                handleSearch(e.target.value);
              }}
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              className={styles.greenBtn}
              onClick={generatePurchaseHistory}
              disabled={isShowGenerateButton === false || loadingIndicator}
              startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {tCommon('Load Purchases')}
            </Button>
          </Grid>
        </Grid>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tCommon('Set filters purchase info title')}
          content={tCommon('Set filters purchase info content')}
        />
      ) : (
        purchases &&
        (filteredPurchases && filteredPurchases.length ? (
          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={filteredPurchases}
              summary={{ plu: 'Total', total: summary.totalAmount, tax: summary.totalTax }}
            />
          </div>
        ) : (
          !loadingIndicator && (
            <Info
              title={tCommon('No purchase data to list')}
              content={tCommon("You haven't any purchases to list with given properties")}
            />
          )
        ))
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(PurchaseItemSummary);
